import { ResizeUpdate, resizeCancel } from './actions';
import * as React from 'react';
import { ActionCreatorWithPayload, dispatchAction } from '@ardoq/rxbeach';
import {
  BOTTOM_BAR_HEIGHT,
  BOTTOM_BAR_BORDER_TOP,
  CONTENT_BAR_HEIGHT,
  MIN_RIGHT_EDITOR_PANE_WIDTH,
  MIN_VIEW_PANE_WIDTH,
  RESIZER_MARGIN,
  SPLIT_WIDTH,
} from './consts';
import {
  AppLayoutState,
  BaseAppLayoutState,
  GetBox,
  ResizeDirection,
} from './types';

export const getIsContextBarVisible = ({
  isViewpointMode,
  isFocusedChrome,
}: Pick<BaseAppLayoutState, 'isViewpointMode' | 'isFocusedChrome'>): boolean =>
  !isViewpointMode && !isFocusedChrome;

type BottomBarVisibilityArgs = Pick<
  BaseAppLayoutState,
  'isGridEditorOpen' | 'isGridEditorPoppedOut' | 'isFocusedChrome'
> & { isGridEditorDisabledInView: boolean };

export const getIsBottomBarVisible = ({
  isGridEditorOpen,
  isGridEditorPoppedOut,
  isGridEditorDisabledInView,
  isFocusedChrome,
}: BottomBarVisibilityArgs): boolean =>
  (isGridEditorPoppedOut || !isGridEditorOpen) &&
  !isGridEditorDisabledInView &&
  !isFocusedChrome;

const BOTTOM_BAR_LAYOUT_HEIGHT = BOTTOM_BAR_HEIGHT + BOTTOM_BAR_BORDER_TOP;

export const getContentBarStyle = ({
  isBottomBarVisible,
}: AppLayoutState): React.CSSProperties => ({
  bottom: isBottomBarVisible ? `${BOTTOM_BAR_LAYOUT_HEIGHT}px` : 0,
});

export const getEditorPaneStyle = ({
  isBottomBarVisible,
  editorHeight,
  isVerticalResizing,
  isEditorPanelVisible,
  isContextBarVisible,
}: AppLayoutState): React.CSSProperties => ({
  height: `${isEditorPanelVisible ? editorHeight : 0}px`,
  zIndex: isVerticalResizing ? 1 : undefined,
  bottom: `${
    (isBottomBarVisible ? BOTTOM_BAR_LAYOUT_HEIGHT : 0) +
    (isContextBarVisible ? CONTENT_BAR_HEIGHT : 0)
  }px`,
});

export const getVerticalResizeStyle = ({
  isBottomBarVisible,
  isHorizontalResizing,
  editorHeight,
  resizeDelta,
  isContextBarVisible,
}: AppLayoutState) => {
  if (isHorizontalResizing) {
    return {};
  }
  const height = Math.abs(resizeDelta) + 2 * RESIZER_MARGIN;

  const bottom =
    (isContextBarVisible ? CONTENT_BAR_HEIGHT : 0) +
    (isBottomBarVisible ? BOTTOM_BAR_LAYOUT_HEIGHT : 0) +
    editorHeight -
    (resizeDelta < 0 ? 0 : resizeDelta);

  return {
    width: '100%',
    height: `${height}px`,
    bottom: `min(100%, ${bottom}px)`,
  };
};

export const getHorizontalResizeStyle = ({
  isBottomBarVisible,
  isEditorPanelVisible,
  isVerticalResizing,
  isContextBarVisible,
  editorHeight,
  resizeDelta,
  isSplitPane,
  leftPaneWidth,
  isRightEditorPaneOpen,
  rightEditorPaneWidth,
}: AppLayoutState): React.CSSProperties => {
  if (!(isSplitPane || isRightEditorPaneOpen) || isVerticalResizing) {
    return {};
  }

  const bottom = getPaneBottom(
    isBottomBarVisible,
    isEditorPanelVisible,
    isContextBarVisible,
    editorHeight
  );

  const width = Math.abs(resizeDelta) + 2 * RESIZER_MARGIN;

  if (isRightEditorPaneOpen) {
    return {
      right: `min(100%, ${
        resizeDelta < 0
          ? rightEditorPaneWidth
          : rightEditorPaneWidth - resizeDelta
      }px)`,
      width: `${width}px`,
      bottom: `${bottom}px`,
    };
  }

  return {
    left: `calc(${(leftPaneWidth * 100).toFixed(1)}% - ${
      resizeDelta < 0 ? Math.abs(resizeDelta) : 0
    }px)`,
    width: `${width}px`,
    bottom: `${bottom}px`,
  };
};

export const getLeftPaneStyle = ({
  isBottomBarVisible,
  isEditorPanelVisible,
  isViewpointMode,
  isContextBarVisible,
  editorHeight,
  isSplitPane,
  leftPaneWidth,
  isRightEditorPaneOpen,
  rightEditorPaneWidth,
}: AppLayoutState) => {
  const bottom = getPaneBottom(
    isBottomBarVisible,
    isEditorPanelVisible,
    isContextBarVisible,
    editorHeight
  );
  if (isViewpointMode) {
    return { bottom };
  }
  if (isRightEditorPaneOpen) {
    return {
      right: `${rightEditorPaneWidth}px`,
      bottom: `${bottom}px`,
    };
  }

  const width = getLeftPaneWidth(isSplitPane, leftPaneWidth);

  return {
    width: isSplitPane
      ? `calc(${(width * 100).toFixed(1)}% - ${SPLIT_WIDTH / 2}px)`
      : '100%',
    bottom: `${bottom}px`,
  };
};

const getLeftPaneWidth = (isSplitPane: boolean, leftPaneWidth: number) =>
  isSplitPane ? leftPaneWidth : 1;

export const getRightPaneStyle = ({
  isBottomBarVisible,
  isEditorPanelVisible,
  isContextBarVisible,
  editorHeight,
  isSplitPane,
  leftPaneWidth,
}: AppLayoutState) => {
  const bottom = getPaneBottom(
    isBottomBarVisible,
    isEditorPanelVisible,
    isContextBarVisible,
    editorHeight
  );

  const left = getLeftPaneWidth(isSplitPane, leftPaneWidth);

  return {
    left: `calc(${(left * 100).toFixed(1)}% + ${SPLIT_WIDTH / 2}px)`,
    right: 0,
    bottom: `${bottom}px`,
  };
};

export const getRightEditorPaneStyle = ({
  isRightEditorPaneOpen,
  rightEditorPaneWidth,
  isBottomBarVisible,
  isEditorPanelVisible,
  isContextBarVisible,
  editorHeight,
}: AppLayoutState): React.CSSProperties => {
  const bottom = getPaneBottom(
    isBottomBarVisible,
    isEditorPanelVisible,
    isContextBarVisible,
    editorHeight
  );

  return {
    width: isRightEditorPaneOpen ? `${rightEditorPaneWidth}px` : '0',
    bottom: `${bottom}px`,
  };
};

const getPaneBottom = (
  isBottomBarVisible: boolean,
  isEditorPanelVisible: boolean,
  isContextBarVisible: boolean,
  editorHeight: number
) =>
  (isContextBarVisible ? CONTENT_BAR_HEIGHT : 0) +
  (isBottomBarVisible ? BOTTOM_BAR_LAYOUT_HEIGHT : 0) +
  (isEditorPanelVisible ? editorHeight : 0);

export const dispatch = (
  event: MouseEvent | React.MouseEvent,
  getBox: GetBox,
  action: ActionCreatorWithPayload<ResizeUpdate>,
  direction: ResizeDirection
) => {
  const { clientX: x, clientY: y } = event;
  const box = getBox();
  const areValidValues =
    box &&
    box.width > 0 &&
    box.height > 0 &&
    Number.isFinite(x) &&
    Number.isFinite(y) &&
    x >= 0 &&
    x <= window.innerWidth &&
    y >= 0 &&
    y <= window.innerHeight;

  if (areValidValues) {
    dispatchAction(action({ x, y, box, direction }));
  } else {
    dispatchAction(resizeCancel());
  }
};

// Returns the panel top in viewport coordinates.
export const getPanelTop = (editorHeight: number, box: DOMRect) => {
  return box.bottom - editorHeight;
};

// Returns the pane left in viewport coordinates.
export const getPaneLeft = (leftPaneWidth: number, box: DOMRect) => {
  return box.left + box.width * leftPaneWidth;
};

type HorizontalResizeBoundArgs = {
  isRightEditorPaneOpen: boolean;
  paneLeft: number;
};

/**
 * How far right the horizontal resizer may be dragged.
 *
 * Floored at zero because a pane already narrower than the minimum - a width persisted before the
 * floor existed - would otherwise produce a negative bound and invert the clamp.
 */
export const getHorizontalMaxResizeDelta = ({
  isRightEditorPaneOpen,
  paneLeft,
  boxRight,
}: HorizontalResizeBoundArgs & { boxRight: number }) => {
  const distanceToRightEdge = boxRight - paneLeft;
  return isRightEditorPaneOpen
    ? Math.max(0, distanceToRightEdge - MIN_RIGHT_EDITOR_PANE_WIDTH)
    : distanceToRightEdge;
};

/**
 * How far left the horizontal resizer may be dragged.
 *
 * The mirror of the bound above, and it has to exist for the same reason: the resize commits
 * `rightEditorPaneWidth - resizeDelta`, so an unfloored left bound of `-(paneLeft - boxLeft)`
 * commits the full pane width to the editor and leaves the view pane at zero - the defect this
 * ticket describes, in the other direction.
 *
 * Capped at zero for the mirror of the other floor's reason: a view pane already narrower than the
 * minimum would otherwise produce a positive bound and invert the clamp.
 */
export const getHorizontalMinResizeDelta = ({
  isRightEditorPaneOpen,
  paneLeft,
  boxLeft,
}: HorizontalResizeBoundArgs & { boxLeft: number }) => {
  const distanceToLeftEdge = paneLeft - boxLeft;
  return isRightEditorPaneOpen
    ? Math.min(0, MIN_VIEW_PANE_WIDTH - distanceToLeftEdge)
    : -distanceToLeftEdge;
};
